/* EARTHLING / two-face shop-logo enhancement — mobile start-up revision 2.
 * Leaves earthling-pixel-logo.js unchanged. It owns the stable fixed anchor.
 * No scroll, wheel, touchmove, canvas sampling or per-frame JS.
 * Bounded start-up retries and stylesheet load events repair late CSS loading.
 * CSS rotates inner surfaces; MutationObserver mirrors anchor styles only
 * when the existing engine reports a real geometry/visibility change.
 */
(function () {
  'use strict';
  var key = '__earthlingShopLogoTurn';
  if (window[key]) { window[key].refresh(); return; }
  if (!window.CSS || !CSS.supports ||
      !CSS.supports('transform', 'scaleX(0.5)') ||
      !window.MutationObserver || !window.matchMedia) return;

  var selector = '.earthling-iridescent-logo--pixel[data-shop-logo-turn]';
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var forced = window.matchMedia('(forced-colors: active)');
  // Touch browsers may synthesise sticky mouseenter/:hover after a tap.
  // Only a real hover-capable mouse can pause; no width/device gate is used.
  var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
  var keyboardNavigation = false;
  var state = null;
  var pending = null;
  var epoch = 0;
  var destroyed = false;
  var listeners = [];
  var bodyObserver = null;
  var startupTimers = [];
  // Exactly the existing approved texture, used only when a browser cannot
  // report the computed background of the generated ::before surface.
  var approvedGradient = 'linear-gradient(115deg, rgb(234.365,235.775,237.655) 0%, rgb(191.157,213.247,224.527) 18%, rgb(191.896,178.266,211.636) 36%, rgb(223.698,202.548,211.478) 52%, rgb(231.986,224.936,194.856) 68%, rgb(202.299,232.379,219.689) 84%, rgb(234.365,235.775,237.655) 100%)';

  function stylesReady(anchor) {
    return !!anchor && window.getComputedStyle(anchor).getPropertyValue('--earthling-shop-turn-css-ready').trim() === '1';
  }
  function clearStartupTimers() {
    startupTimers.forEach(function (timer) { window.clearTimeout(timer); });
    startupTimers = [];
  }
  function watchStartup() {
    if (state || destroyed || startupTimers.length) return;
    [100, 400, 1000, 2500, 5000].forEach(function (delay) {
      startupTimers.push(window.setTimeout(reconcile, delay));
    });
  }

  function listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    listeners.push(function () { target.removeEventListener(type, handler, options); });
  }
  function mediaListen(query) {
    if (query.addEventListener) listen(query, 'change', reconcile);
    else if (query.addListener) {
      query.addListener(reconcile);
      listeners.push(function () { query.removeListener(reconcile); });
    }
  }
  function eligible(anchor, paint) {
    return !destroyed && !reduced.matches && !forced.matches &&
      anchor && paint && document.body &&
      document.documentElement.contains(anchor) &&
      document.documentElement.contains(paint);
  }
  function cssURL(value) { return 'url(' + JSON.stringify(value) + ')'; }

  function clean() {
    epoch += 1;
    if (pending) { pending.image.onload = null; pending.image.onerror = null; }
    pending = null;
    if (!state) return;
    var previous = state;
    state = null;
    previous.observer.disconnect();
    previous.listeners.forEach(function (remove) { remove(); });
    previous.paint.removeAttribute('data-shop-turn-active');
    previous.paint.removeAttribute('data-shop-turn-paused');
    previous.paint.style.removeProperty('--earthling-shop-turn-duration');
    previous.paint.style.removeProperty('--earthling-shop-turn-gradient');
    previous.paint.style.removeProperty('--earthling-shop-shimmer-delay');
    previous.front.remove();
    previous.reverse.remove();
  }

  function pause() {
    if (!state) return;
    var paused = document.hidden || (hoverCapable.matches && state.hovered) ||
      (keyboardNavigation && state.anchor === document.activeElement) ||
      state.paint.style.visibility === 'hidden' || state.paint.style.opacity === '0';
    var value = paused ? 'true' : 'false';
    if (state.paint.getAttribute('data-shop-turn-paused') !== value) {
      state.paint.setAttribute('data-shop-turn-paused', value);
      state.reverse.setAttribute('data-shop-turn-paused', value);
    }
  }
  function mirrorAnchor() {
    if (!state) return;
    ['left', 'top', 'width', 'height', 'opacity', 'visibility'].forEach(function (name) {
      var value = state.paint.style.getPropertyValue(name);
      if (state.reverse.style.getPropertyValue(name) !== value) {
        state.reverse.style.setProperty(name, value);
      }
    });
    pause();
  }
  function attach(anchor, paint, image) {
    if (!eligible(anchor, paint)) return;
    if (!stylesReady(anchor)) { watchStartup(); return; }
    var gradient = window.getComputedStyle(paint, '::before').backgroundImage;
    if (!gradient || gradient === 'none') gradient = approvedGradient;
    var phase = 0;
    try { if (paint.getAnimations) {
      paint.getAnimations().some(function (animation) {
        if (animation.animationName === 'earthlingPixelIridescence') {
          phase = Number(animation.currentTime) || 0;
          return true;
        }
        return false;
      });
    }
    } catch (ignore) { phase = 0; }
    // The setting is DWELL PER FACE, not the full cycle. Default:
    // 3s front + 1.5s turn + 3s reverse + 1.5s turn = 9 seconds.
    // A fresh setting ID prevents an old saved 24s cycle overriding this.
    var hold = Number(anchor.getAttribute('data-shop-logo-hold-seconds')) || 3;
    hold = Math.max(1, Math.min(10, hold));
    var duration = hold * 3;
    var mode = anchor.getAttribute('data-shop-logo-reverse-mode');
    var front = document.createElement('span');
    front.className = 'earthling-shop-turn-face earthling-shop-turn-face--front earthling-shop-turn-face--iridescent';
    front.style.setProperty('--earthling-shop-turn-mask', cssURL(anchor.getAttribute('data-contrast-mask')));
    var reverse = document.createElement('span');
    reverse.className = 'earthling-shop-turn-reverse';
    reverse.setAttribute('aria-hidden', 'true');
    reverse.setAttribute('role', 'presentation');
    reverse.setAttribute('data-reverse-mode', mode);
    // Separate the wide FRE3.0 emblem from the narrow ertheX wordmark.
    reverse.setAttribute('data-shop-turn-variant', anchor.getAttribute('data-shop-logo-turn') || '');
    var back = document.createElement('span');
    back.className = 'earthling-shop-turn-face earthling-shop-turn-face--back';
    if (mode === 'iridescent') {
      back.classList.add('earthling-shop-turn-face--iridescent');
      back.style.setProperty('--earthling-shop-turn-mask', cssURL(image.src));
    } else {
      back.classList.add('earthling-shop-turn-face--artwork');
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.draggable = false;
      back.appendChild(image);
    }
    reverse.appendChild(back);
    [paint, reverse].forEach(function (surface) {
      surface.style.setProperty('--earthling-shop-turn-duration', duration + 's');
      surface.style.setProperty('--earthling-shop-turn-gradient', gradient);
      surface.style.setProperty('--earthling-shop-shimmer-delay', -(phase % 14000) / 1000 + 's');
    });
    paint.appendChild(front);
    paint.setAttribute('data-shop-turn-active', 'true');
    // Two independent, untransformed body-level planes. The front blends
    // with the page; the artwork reverse retains its supplied colours.
    document.body.appendChild(reverse);
    var observer = new MutationObserver(mirrorAnchor);
    state = {anchor: anchor, paint: paint, front: front, reverse: reverse,
      observer: observer,
      hovered: hoverCapable.matches && anchor.matches(':hover'), listeners: []};
    observer.observe(paint, {attributes: true, attributeFilter: ['style']});
    function localListen(type, handler) {
      anchor.addEventListener(type, handler);
      state.listeners.push(function () { anchor.removeEventListener(type, handler); });
    }
    function leave() { if (state) { state.hovered = false; pause(); } }
    if (window.PointerEvent) {
      localListen('pointerenter', function (event) {
        if (state && event.pointerType === 'mouse' && hoverCapable.matches) {
          state.hovered = true; pause();
        }
      });
      localListen('pointerleave', leave);
      localListen('pointercancel', leave);
    } else {
      localListen('mouseenter', function () {
        if (state && hoverCapable.matches) { state.hovered = true; pause(); }
      });
      localListen('mouseleave', leave);
    }
    localListen('focus', pause);
    localListen('blur', pause);
    clearStartupTimers();
    mirrorAnchor();
  }
  function reconcile() {
    if (destroyed) return;
    var anchor = document.querySelector(selector);
    var paint = document.querySelector('.earthling-pixel-iridescent-logo');
    if (!eligible(anchor, paint)) { clean(); return; }
    if (state && state.anchor === anchor && state.paint === paint) {
      mirrorAnchor(); return;
    }
    // Do not make a one-shot attempt while the stylesheet is still loading.
    if (!stylesReady(anchor)) { watchStartup(); return; }
    if (pending && pending.anchor === anchor && pending.paint === paint) return;
    clean();
    var url = anchor.getAttribute('data-shop-logo-reverse');
    if (!url) return;
    var ticket = epoch;
    var image = new Image();
    pending = {anchor: anchor, paint: paint, image: image};
    image.onload = function () {
      if (ticket !== epoch) return;
      pending = null;
      if (eligible(anchor, paint)) attach(anchor, paint, image);
    };
    image.onerror = function () {
      // Leave the approved original logo visible. Never turn to a blank face.
      if (ticket === epoch) pending = null;
    };
    image.src = url;
  }
  function start() {
    if (destroyed || !document.body) return;
    if (!bodyObserver) {
      bodyObserver = new MutationObserver(reconcile);
      // Observe only direct body children, not all page/image mutations.
      bodyObserver.observe(document.body, {childList: true});
    }
    reconcile();
    watchStartup();
  }
  window[key] = {
    refresh: reconcile,
    version: 'mobile-startup-v2',
    // Read-only diagnostic; never changes scrolling, customer data or settings.
    status: function () {
      var anchor = document.querySelector(selector);
      var front = state && state.front;
      return { version: 'mobile-startup-v2', active: !!state,
        reducedMotion: reduced.matches, forcedColors: forced.matches,
        stylesReady: stylesReady(anchor),
        pixelSurface: !!document.querySelector('.earthling-pixel-iridescent-logo'),
        paused: state ? state.paint.getAttribute('data-shop-turn-paused') === 'true' : false,
        animation: front ? window.getComputedStyle(front).animationName : null };
    },
    destroy: function () {
      destroyed = true;
      clearStartupTimers();
      clean();
      if (bodyObserver) bodyObserver.disconnect();
      listeners.forEach(function (remove) { remove(); });
      listeners = [];
      delete window[key];
    }
  };
  mediaListen(reduced);
  mediaListen(forced);
  mediaListen(hoverCapable);
  // Preserve keyboard pausing without freezing the mark after mobile taps
  // or after returning to a shop via browser Back.
  listen(document, 'keydown', function (event) {
    if (event.key === 'Tab') { keyboardNavigation = true; pause(); }
  });
  function pointerInput(event) {
    keyboardNavigation = false;
    if (state && (event.type === 'touchstart' || event.pointerType === 'touch')) {
      state.hovered = false;
    }
    pause();
  }
  if (window.PointerEvent) {
    listen(document, 'pointerdown', pointerInput, { passive: true });
  } else {
    listen(document, 'touchstart', pointerInput, { passive: true });
    listen(document, 'mousedown', pointerInput);
  }
  listen(document, 'visibilitychange', pause);
  listen(window, 'pageshow', start);
  listen(window, 'load', start);
  // A stylesheet load event does not bubble; use capture. This also handles
  // deferred CSS loaders used by theme optimisation apps on mobile.
  listen(document, 'load', function (event) {
    var target = event.target;
    if (target && target.tagName === 'LINK' &&
        ((target.getAttribute('rel') || '').indexOf('stylesheet') !== -1 ||
         target.hasAttribute('data-earthling-shop-turn-styles'))) {
      reconcile();
    }
  }, true);
  listen(document, 'shopify:section:load', reconcile);
  listen(document, 'shopify:section:unload', reconcile);
  if (document.readyState === 'loading') listen(document, 'DOMContentLoaded', start);
  else start();
})();
